import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';
import { faqs } from '@/mocks/faq';

const CONTACT_EMAIL = 'hello@charmingnanny.example.com';

export default function About() {
  useSeo({
    title: '关于与帮助 - 迷人的保姆在线观看平台说明',
    description:
      '了解迷人的保姆在线观看平台的定位、内容范围、版权声明与联系方式，并查看常见问题解答，帮助你更好地使用本站。',
    keywords: '迷人的保姆关于我们,网站帮助,版权声明,常见问题,在线观看说明',
  });

  const [openId, setOpenId] = useState<string | null>(faqs[0]?.id ?? null);

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950 py-10 md:py-14">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="[&_a]:text-background-300 [&_span]:text-background-200 [&_.text-foreground-900]:!text-background-50 [&_.text-foreground-400]:!text-primary-500">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于 / 帮助' }]} />
          </div>
          <h1 className="mt-5 text-2xl md:text-4xl font-bold text-background-50">关于本站 · 帮助中心</h1>
          <p className="mt-3 text-sm md:text-base text-background-300 max-w-3xl leading-relaxed">
            本站是一个专注于《迷人的保姆》及同类型家庭情感剧集的内容展示站点，提供剧集目录、剧情介绍与更新同步展示，
            帮助你方便地了解剧情动态。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="w-full px-4 md:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
          <div className="min-w-0">
            <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                网站简介
              </h2>
              <div className="mt-4 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  本站以《迷人的保姆》这一部都市家庭情感剧为核心，围绕剧集本身搭建了首页推荐、剧集分类、内容详情与播放演示等页面，
                  让观众能够快速浏览剧集目录、查看剧情介绍，并追踪最新的更新动态。
                </p>
                <p>
                  我们希望把“找剧、看剧情、追更新”这三件事做得简单清晰：首页集中展示最新更新与推荐专题，分类页支持按更新状态与
                  集数顺序筛选排序，详情页则完整呈现剧集信息与章节列表。
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2">
                  {[
                    { icon: 'ri-movie-2-line', label: '剧集目录', value: '完整章节' },
                    { icon: 'ri-refresh-line', label: '更新同步', value: '每周更新' },
                    { icon: 'ri-heart-3-line', label: '本地收藏', value: '无需登录' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-md bg-background-50 border border-background-200 p-4">
                      <i className={`${item.icon} text-xl text-primary-700`}></i>
                      <p className="mt-2 text-xs text-foreground-500">{item.label}</p>
                      <p className="mt-0.5 text-sm font-medium text-foreground-900">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div id="contact" className="mt-8 rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                版权声明
              </h2>
              <div className="mt-4 space-y-3 text-sm text-foreground-700 leading-relaxed">
                <p>
                  本站所展示的剧集信息、剧情文字用于内容介绍与交流参考，页面中的图片与素材均为演示用途，相关版权归原权利方所有。
                </p>
                <p>
                  站内播放区域为纯前端功能演示，用于展示观看界面与交互效果，不接入真实视频源，也不提供任何视频下载服务。
                </p>
                <p>
                  如相关内容涉及权利问题，欢迎通过下方联系方式与我们沟通，我们会及时处理。
                </p>
              </div>
            </div>

            <div className="mt-8 rounded-lg bg-secondary-50 border border-secondary-200 p-6 md:p-8">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                联系方式与反馈
              </h2>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                如果你有剧情讨论、内容建议或使用问题，欢迎写信给我们，我们会尽快回复。
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-send-line text-lg"></i>
                {CONTACT_EMAIL}
              </a>
              <p className="mt-3 text-xs text-foreground-500 flex items-center gap-1.5">
                <i className="ri-external-link-line"></i>
                点击邮箱将唤起你本地的邮件客户端。
              </p>
            </div>
          </div>

          <aside className="w-full">
            <div className="lg:sticky lg:top-24">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2 mb-5">
                <i className="ri-question-line text-accent-600"></i>
                常见问题
              </h2>
              <div className="space-y-3">
                {faqs.map((item) => {
                  const open = openId === item.id;
                  return (
                    <div
                      key={item.id}
                      className="rounded-lg bg-background-50 border border-background-200 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenId(open ? null : item.id)}
                        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                        aria-expanded={open}
                      >
                        <span className={`text-sm font-medium ${open ? 'text-primary-700' : 'text-foreground-900'}`}>
                          {item.question}
                        </span>
                        <i
                          className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-foreground-500 shrink-0`}
                        ></i>
                      </button>
                      {open && (
                        <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed animate-fade-in">
                          {item.answer}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 rounded-lg bg-primary-500 p-6 text-background-50 dark:text-foreground-950">
                <h3 className="font-semibold">还没找到想看的剧集？</h3>
                <p className="mt-2 text-sm text-background-100 dark:text-foreground-900/80 leading-relaxed">
                  前往剧集分类，按更新状态与集数顺序筛选《迷人的保姆》及同类型剧集。
                </p>
                <Link
                  to="/category"
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-background-50 text-primary-700 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  前往剧集分类
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}